// Marketing & Com — header section that lives ABOVE the existing
// CommunicationsPanel. Three jobs:
//   1. List the creator's silos with a dynamic marketing URL for each
//      (links land visitors on the marketing page with THAT silo wired
//      in as the "See a live silo" demo target).
//   2. Let the creator write/save custom marketing email templates. Only
//      one template can be "active" at a time (radio behavior).
//   3. "Insert marketing email" buttons next to each silo pull the active
//      template into the Compose Broadcast box below, swapping the
//      `{{silo_link}}` token for that silo's specific marketing URL.
//
// The Compose Broadcast textarea is in CommunicationsPanel — we talk to it
// via a window event ("marketing-insert-broadcast") so we don't have to
// hoist state. CommunicationsPanel listens and overwrites its subject + msg.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone,
  Copy,
  PaperPlaneTilt,
  PlusCircle,
  FloppyDisk,
  Trash,
  PencilSimple,
  WarningCircle,
  Check,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const CALL_HANDLE = "{{silo_link}}";

function buildMarketingUrl(refCode, slug) {
  const origin =
    typeof window !== "undefined" && window.location ? window.location.origin : "";
  if (!refCode || !slug) return "";
  return `${origin}/m/${refCode}/${encodeURIComponent(slug)}`;
}

export default function MarketingPanel({ section = "all" }) {
  const [silos, setSilos] = useState([]);
  const [refCode, setRefCode] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTitle, setComposerTitle] = useState("");
  const [composerBody, setComposerBody] = useState(CALL_HANDLE);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const activeTemplate = useMemo(
    () => templates.find((t) => t.is_active) || null,
    [templates],
  );

  // Load silos + ref code + templates on mount.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [siloRes, refRes, tplRes] = await Promise.all([
          api.get("/silos"),
          api.get("/me/referral-link"),
          api.get("/me/email-templates"),
        ]);
        setSilos(siloRes.data || []);
        setRefCode(refRes.data?.referral_code || "");
        setTemplates(tplRes.data?.templates || []);
      } catch (e) {
        // Feb 21, 2026 (late) — give the toast a stable ID so React's
        // StrictMode double-invocation in dev (and any other accidental
        // double-mount) doesn't stack two identical "Could not load
        // marketing data." pills on top of each other.
        toast.error(e.response?.data?.detail || "Could not load marketing data.", {
          id: "marketing-load-failed",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Marketing link copied.");
    } catch {
      // Fallback for older browsers / Safari quirks.
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success("Marketing link copied.");
      } catch {
        toast.error("Could not copy — long-press the URL to copy it manually.");
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const insertMarketingEmail = (silo) => {
    if (!activeTemplate) {
      toast.error("No template selected. Check the radio next to one above.");
      return;
    }
    const url = buildMarketingUrl(refCode, silo.slug);
    const subject = activeTemplate.title || "Take a look at my silo";
    let body = activeTemplate.body || "";
    if (body.includes(CALL_HANDLE)) {
      body = body.split(CALL_HANDLE).join(url);
    } else {
      // Handle deleted/broken — append the URL at the bottom so the email
      // still goes out useful instead of going out URL-less.
      body = `${body.trim()}\n\n${url}`;
      toast(
        "Heads up: the {{silo_link}} placeholder in your template is missing or broken — pasted the URL at the end instead.",
        { icon: "⚠" },
      );
    }
    // Tell CommunicationsPanel to load this into its compose area.
    window.dispatchEvent(
      new CustomEvent("marketing-insert-broadcast", {
        detail: { subject, message: body, silo_id: silo.id },
      }),
    );
    toast.success(`Loaded marketing email for "${silo.name}" — scroll down to edit & send.`);
  };

  const setActive = async (templateId) => {
    try {
      await api.patch(`/me/email-templates/${templateId}`, { is_active: true });
      setTemplates((prev) =>
        prev.map((t) => ({ ...t, is_active: t.id === templateId })),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update active template.");
    }
  };

  const openComposer = (template = null) => {
    if (template) {
      setEditingId(template.id);
      setComposerTitle(template.title || "");
      setComposerBody(template.body || "");
    } else {
      setEditingId(null);
      setComposerTitle("");
      setComposerBody(CALL_HANDLE + "\n\n");
    }
    setComposerOpen(true);
  };

  const closeComposer = () => {
    setComposerOpen(false);
    setEditingId(null);
    setComposerTitle("");
    setComposerBody("");
  };

  const saveTemplate = async () => {
    const title = composerTitle.trim();
    const body = composerBody.trim();
    if (title.length < 3) {
      toast.error("Title needs at least 3 characters.");
      return;
    }
    if (body.length < 10) {
      toast.error("Body needs at least 10 characters.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const r = await api.patch(`/me/email-templates/${editingId}`, {
          title,
          body,
        });
        setTemplates((prev) => prev.map((t) => (t.id === editingId ? r.data : t)));
        toast.success("Template saved.");
      } else {
        const r = await api.post(`/me/email-templates`, { title, body });
        setTemplates((prev) => [...prev, r.data]);
        toast.success("Template saved.");
      }
      closeComposer();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save template.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (template) => {
    if (template.is_seed) {
      toast.error("The starter template can't be deleted — edit it instead.");
      return;
    }
    if (
      !window.confirm(
        `Delete "${template.title}"? This can't be undone.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/me/email-templates/${template.id}`);
      const refreshed = await api.get("/me/email-templates");
      setTemplates(refreshed.data?.templates || []);
      toast.success("Template deleted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete.");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border hairline rounded-lg p-6 text-sm text-[#6B6A68]" data-testid="marketing-panel-loading">
        Loading marketing tools…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-panel">
      {/* Page-section title — only render when not narrowed to a single section.
          When section="templates" or "silo_links", the header is omitted so
          the Dashboard can interleave with CommunicationsPanel cleanly. */}
      {section === "all" && (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#FDF1ED] text-[#C16E5A] flex items-center justify-center shrink-0">
            <Megaphone size={20} weight="duotone" />
          </div>
          <div>
            <h2 className="font-serif-display text-2xl text-[#1C1B1A]">
              Marketing and Communications
            </h2>
            <p className="text-xs text-[#6B6A68] mt-0.5">
              Per-silo marketing links, custom email templates, and the broadcast composer — all in one place.
            </p>
          </div>
        </div>
      )}

      {/* SILO MARKETING LINKS ============================================= */}
      {(section === "all" || section === "silo_links") && (
      <div className="bg-white border hairline rounded-lg p-6" data-testid="marketing-silo-links">
        <h3 className="font-serif-display text-lg text-[#1C1B1A] mb-1">
          Your silo marketing links
        </h3>
        <p className="text-xs text-[#6B6A68] mb-4 max-w-2xl">
          Each silo has its own dynamic marketing URL. When someone follows it,
          the marketing page's "See a live silo" demo button opens THIS silo
          (instead of the default Cosmic Equivalence). Your referral code rides
          along automatically.
        </p>
        {silos.length === 0 && (
          <div className="text-sm text-[#6B6A68] italic p-4 bg-[#F0F1EE] rounded-md">
            You don't have any silos yet — once you build one, its marketing link will appear here.
          </div>
        )}
        <div className="space-y-2">
          {silos.map((s) => {
            const url = buildMarketingUrl(refCode, s.slug);
            return (
              <div
                key={s.id}
                data-testid={`marketing-silo-row-${s.slug}`}
                className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto_auto] gap-2 md:items-center p-3 border hairline rounded-md hover:border-[#C16E5A]/40 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm text-[#1C1B1A] truncate" title={s.name}>
                    {s.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
                    /s/{s.slug}
                  </div>
                </div>
                <div className="min-w-0">
                  <input
                    type="text"
                    readOnly
                    value={url}
                    data-testid={`marketing-url-${s.slug}`}
                    className="w-full bg-[#F0F1EE] text-[#1C1B1A] border hairline rounded-md px-3 py-2 text-xs font-mono truncate focus:outline-none focus:ring-1 focus:ring-[#C16E5A]"
                  />
                </div>
                <button
                  onClick={() => copyUrl(url)}
                  data-testid={`marketing-copy-${s.slug}`}
                  className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] text-[#1C1B1A] flex items-center gap-1.5"
                >
                  <Copy size={12} weight="duotone" /> Copy
                </button>
                <button
                  onClick={() => insertMarketingEmail(s)}
                  data-testid={`marketing-insert-${s.slug}`}
                  className="text-xs px-3 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] flex items-center gap-1.5 disabled:opacity-50"
                  disabled={!activeTemplate}
                  title={!activeTemplate ? "Check the radio next to a template below first" : ""}
                >
                  <PaperPlaneTilt size={12} weight="duotone" /> Insert marketing email
                </button>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* CUSTOM EMAIL GENERATOR =========================================== */}
      {(section === "all" || section === "templates") && (
      <div className="bg-white border hairline rounded-lg p-6" data-testid="marketing-templates">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-serif-display text-lg text-[#1C1B1A]">
            Custom marketing emails
          </h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
            {templates.length}/30
          </span>
        </div>
        <p className="text-xs text-[#6B6A68] mb-4 max-w-2xl">
          The template with its radio checked is the one that gets loaded when
          you press "Insert marketing email" above. Drop the{" "}
          <code className="px-1 py-0.5 bg-[#F0F1EE] rounded text-[#C16E5A] font-mono">
            {CALL_HANDLE}
          </code>{" "}
          placeholder wherever the silo URL should land — we swap it in
          automatically.
        </p>

        <div className="space-y-2 mb-4">
          {templates.map((t) => {
            const hasHandle = (t.body || "").includes(CALL_HANDLE);
            return (
              <div
                key={t.id}
                data-testid={`template-row-${t.id}`}
                className={`flex items-center gap-3 p-3 border rounded-md transition-colors ${
                  t.is_active
                    ? "border-[#C16E5A] bg-[#FDF1ED]/40"
                    : "hairline hover:border-[#4A5D4E]/40"
                }`}
              >
                <input
                  type="radio"
                  name="active-template"
                  checked={!!t.is_active}
                  onChange={() => setActive(t.id)}
                  data-testid={`template-radio-${t.id}`}
                  className="accent-[#C16E5A] cursor-pointer"
                />
                <button
                  onClick={() => openComposer(t)}
                  data-testid={`template-edit-${t.id}`}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-[#1C1B1A] truncate">
                      {t.title}
                    </span>
                    {t.is_seed && (
                      <span className="text-[9px] px-2 py-0.5 rounded bg-[#F0F1EE] text-[#6B6A68] uppercase tracking-[0.18em]">
                        Starter
                      </span>
                    )}
                    {t.is_active && (
                      <span className="text-[9px] px-2 py-0.5 rounded bg-[#C16E5A] text-white uppercase tracking-[0.18em] inline-flex items-center gap-0.5">
                        <Check size={9} weight="bold" /> Active
                      </span>
                    )}
                    {!hasHandle && (
                      <span
                        title="Missing {{silo_link}} placeholder — the URL will be appended at the end of the email"
                        className="text-[9px] px-2 py-0.5 rounded bg-[#FFF4E0] text-[#A06800] uppercase tracking-[0.18em] inline-flex items-center gap-0.5"
                      >
                        <WarningCircle size={9} weight="duotone" /> No placeholder
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#6B6A68] truncate mt-0.5">
                    {(t.body || "").replace(/\s+/g, " ").slice(0, 120)}…
                  </div>
                </button>
                <button
                  onClick={() => openComposer(t)}
                  data-testid={`template-edit-btn-${t.id}`}
                  className="text-xs px-2.5 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] text-[#1C1B1A] flex items-center gap-1"
                >
                  <PencilSimple size={11} weight="duotone" /> Edit
                </button>
                {!t.is_seed && (
                  <button
                    onClick={() => deleteTemplate(t)}
                    data-testid={`template-delete-${t.id}`}
                    className="text-xs px-2.5 py-1.5 rounded-md border hairline hover:border-[#C16E5A] hover:text-[#C16E5A] text-[#6B6A68] flex items-center gap-1"
                  >
                    <Trash size={11} weight="duotone" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {!composerOpen && (
          <button
            onClick={() => openComposer(null)}
            data-testid="template-new-btn"
            className="w-full text-sm px-4 py-3 rounded-md border-2 border-dashed border-[#C16E5A]/40 hover:border-[#C16E5A] hover:bg-[#FDF1ED]/40 text-[#C16E5A] flex items-center justify-center gap-2 transition-colors"
          >
            <PlusCircle size={16} weight="duotone" /> Customize a new email
          </button>
        )}

        {composerOpen && (
          <div
            className="p-4 border-2 border-[#C16E5A] rounded-md bg-[#FDF1ED]/30 space-y-3"
            data-testid="template-composer"
          >
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-1">
                Title
              </label>
              <input
                type="text"
                value={composerTitle}
                onChange={(e) => setComposerTitle(e.target.value.slice(0, 120))}
                placeholder="e.g., Black Friday silo launch"
                data-testid="template-title-input"
                className="w-full bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#C16E5A]"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-1">
                Body
              </label>
              <textarea
                value={composerBody}
                onChange={(e) => setComposerBody(e.target.value.slice(0, 20_000))}
                rows={10}
                data-testid="template-body-input"
                className="w-full bg-white border hairline rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#C16E5A]"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-[#6B6A68]">
                  Place{" "}
                  <code className="px-1 bg-[#F0F1EE] rounded text-[#C16E5A] font-mono">
                    {CALL_HANDLE}
                  </code>{" "}
                  anywhere you want the silo URL to land. Don't break the
                  braces or the swap won't fire.
                </span>
                <span className="text-[10px] text-[#6B6A68]">
                  {composerBody.length.toLocaleString()}/20,000
                </span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={closeComposer}
                data-testid="template-cancel"
                className="text-sm px-4 py-2 rounded-md border hairline text-[#6B6A68] hover:text-[#1C1B1A]"
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                disabled={saving}
                data-testid="template-save"
                className="text-sm px-4 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] disabled:opacity-50 flex items-center gap-1.5"
              >
                <FloppyDisk size={14} weight="duotone" />
                {saving ? "Saving…" : editingId ? "Save changes" : "Save template"}
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
